import { nullEntityId } from "./id-parser";
import { AnnotationType, InteractionType, QuestionBehavior, QuestionType, } from "./question";
import { formatCurrency, formatCurrencyRange, formatNumber, formatNumberRange, } from "./util/currency";
import { formatDateNoTime, formatMonthAndDay } from "./util/date";
import { formatPhoneNumber } from "./util/phone-formatting";
const formatOptionDefaults = {
    naryPrefix: "",
    naryDelimiter: "; ",
    beginInNewLine: false,
};
// formatAnswers will return a string or null. If an answer (and the corresponding question) should not
// be rendered, it will return null. If an answer should be rendered, it will return a string (the returned
// string might be empty).
//
// NOTE: This function is only used for one purpose and should be removed when we revamp client PDF generation.
export const formatAnswers = (q, pdfBehavior, answers, formatOptions) => {
    const ensureFormatOptions = () => {
        if (!formatOptions) {
            formatOptions = formatOptionDefaults;
        }
        // `formatOptions` should definitely 100% exist now, but this "if" check is necessary
        // to make TypeScript happy...
        if (formatOptions) {
            if (formatOptions.naryPrefix === undefined) {
                formatOptions.naryPrefix = formatOptionDefaults.naryPrefix;
            }
            if (formatOptions.naryDelimiter === undefined) {
                formatOptions.naryDelimiter = formatOptionDefaults.naryDelimiter;
            }
            if (formatOptions.beginInNewLine === undefined) {
                formatOptions.beginInNewLine = formatOptionDefaults.beginInNewLine;
            }
        }
    };
    ensureFormatOptions();
    const interactionType = q.interaction;
    const annotationType = q.annotation;
    // const [pdfBehavior, interactionType, annotationType] =
    //     getAnswerBehaviorsForPerspective(q, perspective);
    if (pdfBehavior === QuestionBehavior.alwaysHide ||
        interactionType === InteractionType.none) {
        // Never show anything if this is an "alwaysHide" question or if the perspective
        // has an interaction type of "none". Nothing else matters.
        return null;
    }
    const forceShowAnswer = q.isPrimaryQuestion() || pdfBehavior === QuestionBehavior.alwaysShow;
    const ensureForcedAnswerOrNull = (formattedAnswer) => {
        // formatted answer may be an empty string. We only want to return
        // an empty string if we are force-showing answers. Otherwise, return null.
        if (formattedAnswer || forceShowAnswer) {
            return formattedAnswer || "";
        }
        return null;
    };
    let answer = (answers || []).find(a => a.questionId == q.id);
    if (!answer) {
        // if there is no answer but this is a primary or alwaysShow question then
        // we still need to return an empty answer to ensure the question shows.
        return ensureForcedAnswerOrNull("");
    }
    const accentText = (text) => {
        return formatOptions && formatOptions.accentNoteText
            ? formatOptions.accentNoteText(text)
            : text;
    };
    let answerText = answer.textAnswer ? answer.textAnswer.trim() : "";
    let newlineWithOptionalIndent = (formatOptions && formatOptions.beginInNewLine === false ? "" : "\n") +
        (formatOptions && formatOptions.indentNewlinesWithSpaces
            ? "&nbsp;&nbsp;&nbsp;&nbsp;"
            : "");
    const prefix = formatOptions?.naryPrefix; // this should never be undefined at this point
    if (q.questionType == QuestionType.binary) {
        const shouldShowAnswerText = q.annotation === AnnotationType.single;
        if (answer.booleanAnswer && shouldShowAnswerText && answerText) {
            return newlineWithOptionalIndent + "Yes - " + accentText(answerText);
        }
        else if (answer.booleanAnswer) {
            return "Yes";
        }
        else {
            return null;
        }
    }
    if (q.questionType == QuestionType.nary) {
        if ((answer.selections || []).length == 0) {
            if (forceShowAnswer) {
                return "";
            }
            return null;
        }
        // This really means "probably" hasPerAnswerAnnotations. There are cases where this
        // won't strictly be accurate, such as when a prompt had a perAnswer annotation and
        // a textAnswer was added but now it doesn't. However, the rest of the code here
        // should handle that anyway, and we should be ok if this isn't strictly accurate
        // in those corner cases.
        const hasPerAnswerAnnotations = annotationType === AnnotationType.perAnswer &&
            answer.selectionAnnotations &&
            answer.selectionAnnotations.some(annotation => annotation.text && annotation.text.trim() != "");
        // helper to append perAnswer annotations if they exist
        const promptTextToShow = (prompt) => {
            let annotationText = "";
            const annotationReallyEnabled = prompt.annotation;
            if (hasPerAnswerAnnotations && annotationReallyEnabled) {
                // if there is a perAnswer annotation, find the text answer for this prompt
                // and we'll concat it to the prompt.
                answer.selectionAnnotations.every(annotation => {
                    if (prompt.id == annotation.selectionId) {
                        // we found the matching annotation. Yeah!
                        if (annotation.text && annotation.text.trim() != "") {
                            // build the annotation text as long as it's not an empty str
                            annotationText = " (" + accentText(annotation.text) + ")";
                        }
                        return false; // we're done searching even if there was an empty annotation
                    }
                    return true; // we didn't find the matching annotation yet...
                });
            }
            return prefix + prompt.title + annotationText;
        };
        // if there are per-answer annotations, show each answer on it's own line;
        // otherwise just separate the list items with a space.
        const joinDelimiter = hasPerAnswerAnnotations && formatOptions?.beginInNewLine
            ? newlineWithOptionalIndent
            : formatOptions?.naryDelimiter; // this should never be undefined at this point
        const preDelimiter = hasPerAnswerAnnotations && formatOptions?.beginInNewLine
            ? newlineWithOptionalIndent
            : "";
        // Need to use filteredPrompts so we'll never show a stale answer if the option *was*
        // previously checked but it has since been removed from the intake UI by making the
        // prompt text be an empty string.
        let naryAnswers = answer.selections
            .map(selection => q.prompts.find(p => p.id == selection))
            .filter(x => x)
            .sort((x, y) => (x.order || 0) - (y.order || 0))
            .map(x => promptTextToShow(x));
        // for questions that have "perAnswer" annotations, we want to start their answers
        // with an extra "joinDelimiter"
        if (hasPerAnswerAnnotations) {
            return preDelimiter + naryAnswers.join(joinDelimiter);
        }
        let singleAnnotationText = "";
        if (answerText && annotationType == AnnotationType.single) {
            singleAnnotationText =
                (formatOptions?.beginInNewLine // formatOptions should be defined 100% of the time
                    ? newlineWithOptionalIndent
                    : formatOptions?.naryDelimiter) +
                    " " +
                    accentText(answerText);
        }
        return (newlineWithOptionalIndent +
            naryAnswers.join(joinDelimiter) +
            singleAnnotationText);
    }
    if (q.questionType == QuestionType.text) {
        if (answerText) {
            return newlineWithOptionalIndent + answerText;
        }
        return ensureForcedAnswerOrNull(answerText);
    }
    if (q.questionType == QuestionType.date) {
        return ensureForcedAnswerOrNull(formatDateNoTime(answer.dateAnswer, ""));
    }
    if (q.questionType == QuestionType.currency) {
        return ensureForcedAnswerOrNull(formatCurrency(answer.numberAnswer));
    }
    if (q.questionType === QuestionType.number &&
        answer.numberAnswer !== null &&
        answer.numberAnswer !== undefined) {
        return ensureForcedAnswerOrNull(formatNumber(answer.numberAnswer));
    }
    if (q.questionType === QuestionType.range &&
        (interactionType === InteractionType.currency ||
            interactionType === InteractionType.rangePrice) &&
        ((answer.numberAnswer !== null && answer.numberAnswer !== undefined) ||
            (answer.numberAnswerHigh !== null && answer.numberAnswerHigh !== undefined))) {
        return ensureForcedAnswerOrNull(formatCurrencyRange(answer.numberAnswer, answer.numberAnswerHigh));
    }
    if (q.questionType === QuestionType.range &&
        (interactionType === InteractionType.number ||
            interactionType === InteractionType.rangeNumber) &&
        ((answer.numberAnswer !== null && answer.numberAnswer !== undefined) ||
            (answer.numberAnswerHigh !== null && answer.numberAnswerHigh !== undefined))) {
        return ensureForcedAnswerOrNull(formatNumberRange(answer.numberAnswer, answer.numberAnswerHigh));
    }
    if (q.questionType === QuestionType.phone) {
        if (answerText !== "") {
            return ensureForcedAnswerOrNull(formatPhoneNumber(answerText));
        }
    }
    return null;
};
export const renderSelections = (question, answer) => {
    const selections = answer.selections || [];
    const selectionAnnotations = answer.selectionAnnotations || [];
    const prompts = question.prompts || [];
    const annotationType = getAnnotation(question);
    const hasPerAnswerAnnotations = annotationType === AnnotationType.perAnswer;
    const rendered = selections
        .map(selection => {
        const prompt = prompts.find(prompt => prompt.id === selection);
        if (prompt) {
            const hasAnnotation = getPromptAnnotation(prompt) && hasPerAnswerAnnotations;
            const annotation = hasAnnotation
                ? selectionAnnotations.find(a => a.selectionId === prompt.id)
                : null;
            const annotationText = (annotation?.text || "").trim();
            const prefix = getPromptTitle(prompt);
            const suffix = annotationText ? ` (${annotationText})` : "";
            return { text: `${prefix}${suffix}`, order: prompt.order };
        }
        return null;
    })
        .filter(textAndOrder => !!textAndOrder)
        .sort((a, b) => (a?.order || 0) - (b?.order || 0))
        .map(textAndOrder => textAndOrder?.text || "");
    const singleAnnotation = renderSingleAnnotation(question, answer);
    if (singleAnnotation) {
        rendered.push(singleAnnotation);
    }
    return rendered;
};
const renderNaryAnswer = (question, answer, formatOptions) => {
    const renderedSelections = renderSelections(question, answer);
    return renderedSelections.length > 0
        ? `${formatOptions.naryPrefix ?? ""}${renderedSelections.join(formatOptions.naryDelimiter ?? formatOptionDefaults.naryDelimiter)}`
        : null;
};
export const renderSingleAnnotation = (question, answer) => {
    const annotationType = getAnnotation(question);
    const annotation = (annotationType === AnnotationType.single ? (answer.textAnswer ?? "") : "").trim();
    return annotation;
};
const renderBooleanAnswer = (question, answer) => {
    const annotation = renderSingleAnnotation(question, answer);
    if (!answer.booleanAnswer && !annotation) {
        return null;
    }
    const prefix = answer.booleanAnswer ? "Yes" : "No";
    const suffix = annotation ? ` - ${annotation}` : "";
    return `${prefix}${suffix}`;
};
export const getInteraction = (question) => {
    return question.interaction;
};
export const getPromptAnnotation = (prompt) => {
    return prompt.annotation;
};
export const getPromptTitle = (prompt) => {
    return prompt.title;
};
export const getAnnotation = (question) => {
    return question.annotation;
};
export const getQuestionTitle = (question) => {
    return question.title || question.id;
};
export const renderAnswer = (question, answer, users, formatOptions = formatOptionDefaults) => {
    formatOptions = {
        ...formatOptionDefaults,
        ...formatOptions,
    };
    switch (question.questionType) {
        case QuestionType.nary: {
            return renderNaryAnswer(question, answer, formatOptions);
        }
        case QuestionType.binary: {
            return renderBooleanAnswer(question, answer);
        }
        case QuestionType.number: {
            return formatNumber(answer.numberAnswer || 0);
        }
        case QuestionType.currency: {
            return formatCurrency(answer.numberAnswer || null);
        }
        case QuestionType.text: {
            return answer.textAnswer || "";
        }
        case QuestionType.calendarDateNoYear: {
            return formatMonthAndDay(answer.calendarDateAnswer);
        }
        case QuestionType.date: {
            let dateAnswer = answer.dateAnswer;
            if (dateAnswer && dateAnswer instanceof Date) {
                dateAnswer = dateAnswer.toISOString();
            }
            if (typeof dateAnswer === "string") {
                dateAnswer = dateAnswer.split("T")[0];
            }
            return formatDateNoTime(answer.numberAnswer, formatDateNoTime(dateAnswer, "-"));
        }
        case QuestionType.range: {
            const interaction = getInteraction(question);
            return interaction === InteractionType.currency ||
                interaction === InteractionType.rangePrice
                ? formatCurrencyRange(answer.numberAnswer, answer.numberAnswerHigh)
                : formatNumberRange(answer.numberAnswer, answer.numberAnswerHigh);
        }
        case QuestionType.user: {
            const userId = answer.idReferenceAnswer;
            if (!userId || userId === nullEntityId()) {
                return null;
            }
            const user = users[userId];
            return user ? user.name : userId;
        }
        case QuestionType.phone: {
            const value = answer.textAnswer || "";
            if (!value || value.trim() === "") {
                return null;
            }
            return formatPhoneNumber(value);
        }
        default: {
            return null;
        }
    }
};
export const dateAnswerToStringDateAnswer = (unparsed) => {
    if (!unparsed) {
        return "";
    }
    if (typeof unparsed === "object" && unparsed instanceof Date) {
        return unparsed.toISOString();
    }
    if (typeof unparsed === "string") {
        return unparsed;
    }
    if (typeof unparsed === "object" && typeof unparsed["toString"] === "function") {
        return unparsed.toString();
    }
    return "";
};
export const answerToGqlAnswer = (answer) => {
    if (!answer) {
        return answer;
    }
    return {
        ...answer,
        dateAnswer: dateAnswerToStringDateAnswer(answer.dateAnswer),
    };
};
//# sourceMappingURL=answer-util.js.map